"use client"

import { useState, useEffect, useCallback } from "react"
import Table from "../modules/table/table"
import Button from "../modules/Button/Button"
import Modal from "../modules/Modal/Moadal"
import styles from "../../../pages/inventory/InventoryDashboardPage.module.css"
import InfoCard from "../modules/InfoCard/InfoCard"
import StatusSelector from "../detail/StatusSelector"
import { getClientBulkOrders, updateClientBulkOrderStatus, fetchByUrl } from "../../../services/inventory/detail"
import { jsPDF } from "jspdf"
import { parseDateSafe } from "../../../utils/dateFormatters"

// --- OrderPdfExporter (kept in same file for convenience) ---
const hexToRgb = (hex) => {
  const clean = (hex || "#f3f4f6").replace("#", "")
  const bigint = Number.parseInt(clean, 16) || 0
  return [(bigint >> 16) & 255 || 243, (bigint >> 8) & 255 || 243, bigint & 255 || 246]
}

const OrderPdfExporter = {
  generate: (order, statusColorMap = {}, options = {}) => {
    try {
      if (!order) {
        console.error("OrderPdfExporter: No order data provided")
        return
      }

      const {
        size = "a4",
        orientation = "portrait",
        margins = { top: 14, left: 14, right: 14, bottom: 14 },
        type = "dropship",
        sections = null,
        fileName = null,
      } = options

      const pdf = new jsPDF({ unit: "mm", format: size, orientation })
      // polyfill/fallbacks for jsPDF builds that may not include plugins
      if (typeof pdf.roundedRect !== 'function') {
        pdf.roundedRect = function (x, y, w, h) {
          // fallback to normal rect (no rounding)
          return this.rect(x, y, w, h);
        }
      }
      if (typeof pdf.getTextWidth !== 'function') {
        pdf.getTextWidth = function (txt) {
          // rough approximation using font size
          const size = this.getFontSize ? this.getFontSize() : 10
          return (String(txt).length * size) / 2.5
        }
      }
      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()
      const margin = margins.left

      let cursorY = margins.top

      pdf.setFontSize(18)
      pdf.setFont("helvetica", "bold")
      const title = order.requestNumber ? "Dropship Request" : "Order Details"
      pdf.text(title, pageWidth / 2, cursorY, { align: "center" })

      cursorY += 8
      pdf.setFontSize(10)
      pdf.setFont("helvetica", "normal")

      const orderNumber = order.requestNumber || order.orderNumber || order.id || "-"
      const orderDate = order.requestDate || order.orderDate || new Date().toLocaleDateString()

      pdf.text(`Order #: ${orderNumber}`, margin, cursorY)
      pdf.text(`Date: ${parseDateSafe(orderDate)?.toLocaleDateString() || new Date().toLocaleDateString()}`, pageWidth - margin, cursorY, {
        align: "right",
      })

      cursorY += 6
      pdf.setDrawColor(210)
      pdf.setLineWidth(0.25)
      pdf.line(margin, cursorY, pageWidth - margin, cursorY)
      cursorY += 8

      const columnGap = 8
      const colWidth = (pageWidth - margin * 2 - columnGap) / 2
      const leftX = margin
      const rightX = margin + colWidth + columnGap

      const drawKeyValue = (x, y, key, value) => {
        const keyWidth = 36
        pdf.setFontSize(9)
        pdf.setFont("helvetica", "bold")
        pdf.text(`${key}:`, x, y)
        pdf.setFont("helvetica", "normal")
        const valueLines = pdf.splitTextToSize(String(value || "-"), colWidth - keyWidth - 4)
        pdf.text(valueLines, x + keyWidth + 2, y)
        return y + valueLines.length * 5
      }

      pdf.setFontSize(12)
      pdf.setFont("helvetica", "bold")
      pdf.text("Shipping Information", leftX, cursorY)
      pdf.text("Device Information", rightX, cursorY)
      cursorY += 6

      let leftY = cursorY + 6
      leftY = drawKeyValue(leftX, leftY, "Name", order.patientName || order.firstName || "-")
      leftY = drawKeyValue(leftX, leftY + 2, "Address", order.address || "-")
      leftY = drawKeyValue(leftX, leftY + 2, "City", order.city || "-")
      leftY = drawKeyValue(leftX, leftY + 2, "State", order.state || "-")
      leftY = drawKeyValue(leftX, leftY + 2, "Zip", order.zipCode || "-")

      if (order.location) {
        leftY = drawKeyValue(leftX, leftY + 2, "Location", order.location)
      }
      if (order.tenantName) {
        leftY = drawKeyValue(leftX, leftY + 2, "Tenant", order.tenantName)
      }
      if (order.orderedBy) {
        leftY = drawKeyValue(leftX, leftY + 2, "Ordered By", order.orderedBy)
      }

      let rightY = cursorY + 6
      rightY = drawKeyValue(rightX, rightY, "Device", order.deviceName || order.deviceType || "-")
      rightY = drawKeyValue(rightX, rightY + 2, "Quantity", String(order.quantity || 1))

      if (order.gatewayOnly !== undefined) {
        rightY = drawKeyValue(rightX, rightY + 2, "Gateway Only", order.gatewayOnly ? "Yes" : "No")
      }
      if (order.requiredSignature !== undefined) {
        rightY = drawKeyValue(rightX, rightY + 2, "Signature Required", order.requiredSignature ? "Yes" : "No")
      }
      if (order.devices) {
        rightY = drawKeyValue(rightX, rightY + 2, "Total Devices", String(order.devices))
      }

      const contentBottom = Math.max(leftY, rightY) + 6

      const statusKey = order.status || "N/A"
      const statusColor = (statusColorMap[statusKey] && statusColorMap[statusKey].color) || "#f3f4f6"
      const statusTextColor = (statusColorMap[statusKey] && statusColorMap[statusKey].text) || "#374151"
      const statusLabel = String(order.rawStatusLabel || order.status || "-")

      pdf.setFontSize(8)
      pdf.setFont("helvetica", "bold")
      const textWidth = pdf.getTextWidth(statusLabel)
      const badgePadding = 3
      const maxBadgeWidth = 60
      const badgeWidth = Math.min(maxBadgeWidth, textWidth + badgePadding * 2)
      const badgeHeight = 9
      const badgeX = pageWidth - margin - badgeWidth - 2
      const badgeY = contentBottom
      const cornerRadius = 2

      const badgeRgb = hexToRgb(statusColor)
      pdf.setFillColor(badgeRgb[0], badgeRgb[1], badgeRgb[2])

      pdf.roundedRect(badgeX, badgeY, badgeWidth, badgeHeight, cornerRadius, cornerRadius, "F")

      try {
        const textRgb = hexToRgb(statusTextColor)
        pdf.setTextColor(textRgb[0], textRgb[1], textRgb[2])
      } catch (e) {
        pdf.setTextColor(0, 0, 0)
      }

      pdf.text(statusLabel, badgeX + badgeWidth / 2, badgeY + badgeHeight / 2 + 0.6, {
        align: "center",
        baseline: "middle",
      })

      pdf.setTextColor(0, 0, 0)

      let bottomY = badgeY + badgeHeight + 8
      if (order.notes) {
        pdf.setDrawColor(230)
        pdf.setFillColor(250, 250, 250)
        const notesLines = pdf.splitTextToSize(order.notes, pageWidth - margin * 2 - 8)
        const notesHeight = Math.min(60, notesLines.length * 5 + 8)
        pdf.roundedRect(margin, bottomY, pageWidth - margin * 2, notesHeight, 2, 2, "F")

        pdf.setFont("helvetica", "bold")
        pdf.text("Notes:", margin + 4, bottomY + 6)
        pdf.setFont("helvetica", "normal")
        pdf.text(notesLines, margin + 4, bottomY + 12)
        bottomY += notesHeight + 8
      }

      pdf.setDrawColor(210)
      pdf.setLineWidth(0.15)
      pdf.line(margin, pageHeight - margins.bottom - 6, pageWidth - margin, pageHeight - margins.bottom - 6)

      const fileNameToUse =
        fileName || `${type}-${order.requestNumber || order.orderNumber || order.id || Date.now()}.pdf`

      pdf.save(fileNameToUse)
      return pdf
    } catch (err) {
      console.error("OrderPdfExporter Error:", err)
      throw err
    }
  },
}

export default OrderPdfExporter

